
// Vue reactivity
import { ref, defineComponent, computed, watch, reactive, onMounted } from 'vue';

// icons
import { add, close, checkmark, arrowUp,  arrowForward, arrowBack, trashOutline, } from 'ionicons/icons';

// components
import { IonHeader, IonToolbar, IonTitle, IonContent, IonFooter, IonRow, IonCol, IonAccordion, IonAccordionGroup,
        IonItem, IonLabel, IonIcon, IonButtons, IonButton,
        IonSearchbar, IonSegment, IonSegmentButton, IonList, IonSelect, IonSelectOption,
        IonCard, IonCardHeader, IonCardSubtitle, IonCardContent, IonChip, IonText, IonCardTitle, IonGrid, IonCheckbox,
        IonInput, IonDatetimeButton, IonDatetime, IonModal,
        loadingController, modalController, } from '@ionic/vue';

// composables / services
import { useI18n } from 'vue-i18n';
import { utils } from '@/composables/utils';
import { useStore } from '@/store';

// types
import { User, Session, Lesson, } from '@/types';
import { alertController } from '@ionic/core';
import TeacherService from '@/services/TeacherService';

export default defineComponent({
  props: ["targetAnchorEvId", "showHeader", "suggestedDuration", "ab4IntakeId"],
  components: { IonHeader, IonToolbar, IonTitle, IonContent, IonFooter, IonRow, IonCol, IonAccordion, IonAccordionGroup,
                IonItem, IonLabel, IonIcon, IonButtons, IonButton, IonChip, IonText, IonGrid, IonCheckbox,
                IonSearchbar, IonSegment, IonSegmentButton, IonList, IonSelect, IonSelectOption,
                IonCard, IonCardHeader, IonCardSubtitle, IonCardContent, IonCardTitle,
                IonInput, IonDatetimeButton, IonDatetime, IonModal, },
  setup(props) {
    // methods or filters
    const store = useStore();
    const { openModal, presentPrompt, getProxyImgLink, openImageModal, presentToast, formatDate } = utils();
    const { t } = useI18n();

    //const selectedABSEventFilter = ref("all");
    const user = computed(() => store.state.user);
    const isAB4User = computed(() => store.getters.isAB4User);
    const lesson = computed<Lesson>(() => store.getters.getLessonByAnchorEventId(props.targetAnchorEvId));
    const userABSIntakeSessions = computed<Session[]>(() => store.getters.userABSIntakeSessions);
    const userSchoolAB4Intakes = computed(() => store.getters.userSchoolAB4Intakes);
    const selectedIntakeId = ref(userSchoolAB4Intakes.value[0]?.id);
    const getFilteredSessions = () => {
      const { targetAnchorEvId, ab4IntakeId } = props;
      const checkIntakeId = selectedIntakeId.value || ab4IntakeId;
      const res = userABSIntakeSessions.value.filter(s => (s.anchorEventId == targetAnchorEvId && (!checkIntakeId || checkIntakeId == s.absIntakeId)));
      //return res.map(s => ({ ...s }));
      for (const ev of res) ev.date = ev.date ? formatDate(ev.date, "YYYY-MM-DD") : null;
      return res;
    }

    const getUpdatedSessions = () => {
      // Only update sessions with value updated
      const checkFields = ["date", "venue", "numOfStudents", "studentForms", "classes", "arrivalTime", "roomAvailableTime", "startTime", "endTime"];
      const updatedSessions: any = [];
      for (const fs of getFilteredSessions()) {
        const originalSession = userABSIntakeSessions.value.find(s => s.id == fs.id) || {};
        for (const field of checkFields) {
          if (fs[field] != originalSession[field]) {
            updatedSessions.push(fs);
            break;
          }
        }
      }
      return updatedSessions;
    }
    const presentSavePrompt = async (callback) => {
      // prompt save or not before leave
      const alert = await alertController.create({
        message: "Save before leaving?",
        buttons: [
          {
            text: t('no'),
            handler: () => {
              modalController.dismiss({});
              return true;
            },
          },
          {
            text: t('yes'),
            handler: callback,
          }
        ]
      });
      return alert.present();
    }
    const saveUpdatedSessions = async (targetSessions = null) => {
      //const updatedSessions = getUpdatedSessions();
      const updatedSessions = targetSessions || getFilteredSessions();
      const loading = await loadingController.create({ });
      await loading.present();
      for (const s of updatedSessions) {
        const teacherResponse = {
          eventName: s.displayName,
          estNumOfStudents: s.numOfStudents,
          preferredSessionDates: s.date,
          preferredSessionTime: s.startTime,
          preferredVenue: s.venue,
          studentForms: s.studentForms,
          classes: s.classes,
          arrivalTime: s.arrivalTime,
          roomAvailableTime: s.roomAvailableTime,
          endTime: s.endTime,
          response: "Change Request",
        };
        TeacherService.upsertTeacherResponse(s, "", teacherResponse, user.value.phone, user.value.schoolId, user.value.id);
      }
      store.commit('upsertSessions', updatedSessions);
      presentToast('Your change request has been submitted successfully.')
      loading.dismiss();
    }
    const closeModal = async () => {
      const updatedSessions = getUpdatedSessions();
      if (updatedSessions.length > 0) {
        presentSavePrompt(async () => {
          await saveUpdatedSessions();
          await modalController.dismiss({}); // nothing changed, leave directly
        });
      } else {
        await modalController.dismiss({}); // nothing changed, leave directly
      }
    }

    // return variables & methods to be used in template HTML
    return {
      // icons
      add, close, checkmark, arrowUp, arrowForward, arrowBack,
      trashOutline,

      // variables
      user,
      isAB4User,
      lesson,
      userSchoolAB4Intakes, selectedIntakeId,

      // methods
      t,
      closeModal,
      getProxyImgLink,
      openImageModal,
      saveUpdatedSessions,
      getUpdatedSessions,
      getFilteredSessions,
      getSeatingPlanImgLink: () => {
        const { targetAnchorEvId: id } = props;
        const slideId = id == 'abs-session1' ? 'g24fff357604_0_102' : 'g2e993164d1c_2_49';
        return getProxyImgLink(`https://docs.google.com/presentation/d/1B5qHJu7ZV72CUOMD4VSoJ2Ptg82prAM0t1hnzxmrp0c/export/jpeg?id=1B5qHJu7ZV72CUOMD4VSoJ2Ptg82prAM0t1hnzxmrp0c&pageid=${slideId}`);
      },
      isPastEvent: (ev: any) => {
        return ev.userResponse?.attended != 'Yes' && new Date().getTime() > new Date(ev.endTimeStr).getTime()+60*60000;
      },
      submitForm: () => {
        presentPrompt(t('confirmSubmit'), () => {
          closeModal();
        });
      },
    }
  },
});
